// API client for events - replaces hardcoded data
// Smart API URL detection
const getApiBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production, try to use the same domain for API
  if (process.env.NODE_ENV === 'production') {
    return '/api'; // Relative URL - same domain
  }
  
  // Development fallback
  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

// Fallback data for when API is unavailable
const FALLBACK_EVENTS = [
  {
    _id: 'fallback-1',
    title: 'Brokz Nieruchomości Anniversary',
    description: 'Celebrate the Brokz Nieruchomości 1 Year Anniversary at the elegant Pawilon Krzyżanowskiego, ul. Garbary 96 — an evening of style, champagne, and celebration.',
    shortDescription: 'Celebrate the Brokz Nieruchomości 1 Year Anniversary',
    price: 150,
    currency: 'PLN',
    date: new Date('2025-10-03T18:00:00').toISOString(),
    durationMinutes: 300,
    capacity: 80,
    availableSeats: 45,
    dressCode: 'Elegant',
    inclusions: ['Live music', 'Champagne', 'Fine appetizers', 'Networking opportunities'],
    specialFeatures: ['Red Carpet', 'Professional photography', 'Networking'],
    schedule: [
      { time: new Date('2025-10-03T18:00:00').toISOString(), activity: 'Doors open' },
      { time: new Date('2025-10-03T18:30:00').toISOString(), activity: 'Official Welcoming' },
      { time: new Date('2025-10-03T19:30:00').toISOString(), activity: 'Opening Champagne' },
      { time: new Date('2025-10-03T21:00:00').toISOString(), activity: 'Networking & Music' }
    ],
    thumbnail: '/assets/BrokzFront.jpg',
    images: ['/assets/BrokzFront.jpg', '/assets/BrokzHero.png'],
    videos: [],
    tags: ['Brokz', 'Nieruchomości', 'Anniversary', 'Formal Event'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    _id: 'fallback-2',
    title: 'Nightmare At Pawilon Krzyżanowskiego',
    description: 'A haunting Halloween experience at Pawilon Krzyżanowskiego. Join us for a night of mystery, thrills, and unforgettable moments in our historic venue.',
    shortDescription: 'A haunting Halloween experience at Pawilon Krzyżanowskiego',
    price: 150,
    currency: 'PLN',
    date: new Date('2025-10-31T20:00:00').toISOString(),
    durationMinutes: 300,
    capacity: 100,
    availableSeats: 85,
    dressCode: 'Halloween costume encouraged',
    inclusions: ['Live entertainment', 'Themed decorations', 'Drinks', 'Photo opportunities'],
    specialFeatures: ['Haunted venue tour', 'Live performances', 'DJ music'],
    schedule: [
      { time: new Date('2025-10-31T20:00:00').toISOString(), activity: 'Doors open' },
      { time: new Date('2025-10-31T21:00:00').toISOString(), activity: 'Halloween entertainment begins' },
      { time: new Date('2025-10-31T23:00:00').toISOString(), activity: 'Midnight surprise' },
      { time: new Date('2025-11-01T01:00:00').toISOString(), activity: 'Event ends' }
    ],
    thumbnail: '/assets/HalloweenPoster.png',
    images: ['/assets/HalloweenPoster.png'],
    videos: [],
    tags: ['Halloween', 'Party', 'Costume', 'Night Event'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

class EventsApi {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache', // Prevent Safari caching issues
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log('Fetching from:', url); // Debug log
      const response = await fetch(url, config);
      
      if (!response.ok) {
        console.error('API response not OK:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API data received:', data.length, 'events'); // Debug log
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      
      // Safari-specific error handling
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.warn('Network error - this might be a CORS or connectivity issue');
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      }
      
      throw error;
    }
  }

  // Get all events with fallback (language-aware)
  async getAllEvents(language = 'en') {
    try {
      const events = await this.request(`/events?lang=${language}`);
      console.log('✅ Successfully fetched', events.length, 'events from API');
      
      // If API returns an error object instead of array, throw error
      if (events.error) {
        throw new Error(events.error);
      }
      
      // If API returns 0 events, use fallback
      if (events.length === 0) {
        console.warn('⚠️ API returned 0 events, using fallback data');
        return FALLBACK_EVENTS;
      }
      
      return events;
    } catch (error) {
      console.error('❌ API ERROR - Failed to fetch events:', error.message);
      console.error('Full error:', error);
      console.warn('Using fallback events due to API error');
      return FALLBACK_EVENTS;
    }
  }

  // Get single event by ID
  async getEventById(id) {
    return this.request(`/events/${id}`);
  }

  // Get upcoming events only
  async getUpcomingEvents() {
    return this.request('/events/upcoming');
  }

  // Create new event (admin)
  async createEvent(eventData) {
    return this.request('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  // Update event (admin)
  async updateEvent(id, eventData) {
    return this.request(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
  }

  // Delete event (admin)
  async deleteEvent(id) {
    return this.request(`/events/${id}`, {
      method: 'DELETE',
    });
  }
}

// Create and export singleton instance
const eventsApi = new EventsApi();
export default eventsApi;